import React from "react";
import beforeMultipleTabsImage from "../../assets/img/landing-v2/before-multiple-tabs.png";
import afterLessonLaunchpadVideo from "../../assets/img/landing-v2/after-lesson-launchpad.mp4";
import afterLessonLaunchpadPoster from "../../assets/img/landing-v2/after-lesson-launchpad-poster.jpg";
import classStylesPreviewVideo from "../../assets/img/landing-v2/class-styles-preview.mp4";
import classStylesPreviewPoster from "../../assets/img/landing-v2/class-styles-preview-poster.jpg";
import teacherHubScheduleManagerImage from "../../assets/img/legacy-schedule-july-2026-conversion-schedule-manager.png";
import welcomeMusicPreviewAudio from "../../assets/sounds/Cavaquinho.mp3";
import BIRTHDAY_AUDIO_TRACKS from "../../config/birthdayAudioTracks";

const birthdayPreviewAudio = BIRTHDAY_AUDIO_TRACKS[0]?.file || "";

export const HERO = {
  eyebrow: "The Classroom Automation Platform for Teachers.",
  headlineHighlight: "Automate",
  headlineRest: " your classroom routines.",
  headlineSecondLinePrefix: "Focus on ",
  headlineSecondLineHighlight: "teaching",
  subheadline:
    "Lesson Launchpad™ automatically manages timers, agendas, reminders, transitions, music, and classroom displays so you can spend less time managing logistics and more time teaching.",
  primaryCta: "Start Free 30-Day Trial",
  screenshotLabel:
    "Screenshot needed: Lesson Launchpad classroom screen showing agenda, timer, clock, class info, and clean classroom display.",
  videoLabel:
    "Video needed: 15 to 20 second demo showing a class transition, timer starting, agenda updating, reminder appearing, and music starting automatically.",
  callouts: [
    { id: "timers", text: "Auto-start timers", position: "top-left" },
    { id: "classes", text: "Advance classes automatically", position: "top-right" },
    { id: "reminders", text: "Show reminders at the right time", position: "middle-left" },
    { id: "agendas", text: "Display agendas and materials", position: "middle-right" },
    { id: "music", text: "Start transition music", position: "bottom-center" },
  ],
};

export const BEFORE_AFTER = {
  headline: "Replace all those browser tabs with one screen.",
  supportingCopy: "Timers, slides, reminders, agendas, and resources — all working together automatically.",
  before: {
    title: "Before",
    items: [
      "Too many browser tabs",
      "YouTube timers",
      "Sticky notes",
      "Phone alarms",
      "Google Slides",
      "Music controls",
      "Whiteboard agendas",
      "Manual reminders",
    ],
    imageSrc: beforeMultipleTabsImage,
    imageAlt:
      "Computer desktop crowded with dozens of browser tabs, Google Slides, a YouTube timer, Spotify, sticky notes, a whiteboard agenda, and reminder apps.",
    imageLabel: "Screenshot needed: example of multiple tabs/tools a teacher might juggle.",
  },
  after: {
    title: "After",
    items: [
      "One screen, one tab",
      "Automated timers",
      "Scheduled reminders",
      "Class transitions",
      "Interactive agendas",
      "Unified resources",
      "Built-in announcements",
      "Everything in sync",
    ],
    videoSrc: afterLessonLaunchpadVideo,
    videoPoster: afterLessonLaunchpadPoster,
    videoAlt:
      "Lesson Launchpad classroom display with timer, agenda, music reminder, and class schedule.",
  },
};

export const PRESENT_SCREEN_MOCK = {
  headlineHighlight: "Automate",
  headlineRest: " your classroom screen.",
  headlineSecondLinePrefix: "Focus on ",
  headlineSecondLineHighlight: "teaching",
  subheadline:
    "Lesson Launchpad coordinates your lesson content and classroom routines around your schedule, automatically — so you can focus on teaching.",
  primaryCta: "Start Your Free Trial",
  ctaSubtext: "Free for 30 days. No credit card required.",
  scheduleLabel: "Today's Schedule",
  scheduleValue: "A Day",
  marqueeText: "Welcome to Science! Today: warm-up, lab setup, partner notes, and exit ticket.",
  editorTabs: ["Text Editor...", "Text Editor..."],
};

export const HERO_AUTOMATION_CALLOUTS = {
  "warmup-reminder": {
    items: [
      {
        id: "warmup-reminder",
        title: "Automatic Reminders!",
        tone: "reminder",
        icon: "fa-bell",
        placement: "left",
      },
    ],
  },
  birthday: {
    items: [
      {
        id: "birthday",
        title: "Automatic Birthdays!",
        tone: "birthday",
        icon: "fa-birthday-cake",
        placement: "top-right",
      },
    ],
  },
  "agenda-progress": {
    items: [
      {
        id: "agenda-progress",
        title: "Automatic Agenda",
        tone: "agenda",
        icon: "fa-list-alt",
        placement: "agenda-right",
      },
    ],
  },
  "agenda-widgets": {
    items: [
      {
        id: "agenda-widgets",
        title: "Helpful Widgets!",
        tone: "widget",
        icon: "fa-magic",
        placement: "below-timer",
      },
    ],
  },
  cleanup: {
    items: [
      {
        id: "cleanup",
        title: "Automatic Timers!",
        tone: "timer",
        icon: "fa-clock-o",
        placement: "center-right",
      },
    ],
  },
  "next-class": {
    items: [
      {
        id: "next-class",
        title: "Classes Change Automatically!",
        tone: "transition",
        icon: "fa-exchange",
        placement: "top-right",
      },
    ],
  },
  "browser-window": {
    showTogether: true,
    items: [
      {
        id: "browser-window",
        title: "Runs Right in Your Browser!",
        tone: "browser",
        icon: "fa-globe",
        placement: "top-center",
      },
      {
        id: "browser-nothing-to-download",
        title: "Nothing to download!",
        tone: "download",
        icon: "fa-check-circle",
        placement: "browser-companion",
        popDelayMs: 1380,
      },
    ],
    repeatCallout: {
      id: "browser-loop-repeat",
      title: "Repeating",
      tone: "repeat",
      icon: "fa-repeat",
      placement: "center",
      showAfterMs: 5400,
    },
  },
};

export const AUTOMATION_TIMELINE = {
  headline: "Your classroom, on autopilot.",
  supportingCopy:
    "Build your daily routine once, then let Lesson Launchpad run the right content, timers, and reminders at exactly the right time.",
  videoSrc: "",
  videoPoster: "",
  videoPlaceholder:
    "Add the finished classroom automation video here. Timeline clicks are already wired to seek to matching timestamps.",
  events: [
    {
      id: "class-change",
      time: "9:00 AM",
      label: "Auto class change",
      description: "At 9:00, your display switches to the next class. No clicks or scrambling between periods.",
      videoTime: 0,
    },
    {
      id: "welcome-music",
      time: "9:00:30 AM",
      label: "Welcome music starts",
      description: "Transition music starts as students arrive, so class begins with the right energy.",
      videoTime: 8,
      audioSrc: welcomeMusicPreviewAudio,
    },
    {
      id: "warmup-timer",
      time: "9:01 AM",
      label: "Warmup timer appears",
      description: "A Do Now timer appears on the board so students know exactly how long they have.",
      videoTime: 15,
    },
    {
      id: "warmup-reminder",
      time: "9:03 AM",
      label: "Timer ends + reminder",
      description: "When time's up, the music stops and your next instruction appears, with no manual cueing needed.",
      videoTime: 24,
    },
    {
      id: "birthday",
      time: "9:04 AM",
      label: "Birthday announcement",
      description: "A student birthday message shows up on schedule, without you having to remember.",
      videoTime: 32,
      audioSrc: birthdayPreviewAudio,
      audioCategory: "birthday",
    },
    {
      id: "agenda-progress",
      time: "9:05-9:43 AM",
      label: "Agenda progresses",
      description: "Your agenda advances step by step through the period, keeping everyone on track.",
      videoTime: 40,
    },
    {
      id: "cleanup",
      time: "9:43 AM",
      label: "Cleanup timer appears",
      description: "Two minutes before the bell, a cleanup timer appears so students wrap up on time.",
      videoTime: 52,
    },
    {
      id: "next-class",
      time: "9:45 AM",
      label: "Next class loads",
      description: "When cleanup ends, your screen is already ready for the next class.",
      videoTime: 64,
    },
  ],
  footerLineItems: [
    "No tab switching.",
    "No forgotten timers.",
    "No mental juggling.",
  ],
};

export const CONSISTENCY = {
  headlinePhrases: [
    "Consistent Routines.",
    "Calmer Classrooms.",
    "Less Teacher Stress.",
  ],
  paragraph:
    "When students know where to look, what comes next, and how much time they have, the whole room feels more settled. Lesson Launchpad keeps your lesson content and classroom routines moving automatically, even when the day gets hectic.",
  testimonial: {
    quote:
      "Lesson Launchpad makes our routines feel automatic. My students know where to look, what's coming next, and how much time they have left — which means fewer interruptions, smoother transitions, and less stress for me.",
  },
};

export const USE_EXISTING = {
  headline: "Use the resources you already love.",
  paragraphs: [
    "Already using Google Slides, Canva, PDFs, PowerPoint, Docs, videos, or other lesson materials? Bring them with you — no rebuilding, no tab juggling.",
    "Lesson Launchpad pulls your existing resources together on one classroom screen, so you can keep using what works.",
  ],
  strongLine: "No need to start from scratch.",
  chips: [
    { label: "Google Slides", id: "google-slides", badge: "Slides" },
    { label: "PDFs", id: "pdf", badge: "PDF" },
    { label: "PowerPoint", id: "powerpoint", badge: "P" },
    { label: "Google Docs", id: "google-docs", badge: "Docs" },
    { label: "Canva", id: "canva", badge: "C" },
    { label: "YouTube", id: "youtube", badge: "▶" },
  ],
  imageLabel:
    "Screenshot needed: Lesson Launchpad screen showing embedded Google Slides or lesson material alongside agenda/timer.",
};

export const TOOLS_WIDGETS = {
  headline: "All your classroom tools, ready to go.",
  subheading:
    "Keep the classroom helpers you reach for all day in one launch-ready place.",
  widgetIntro: "Timers, agendas, notes, sound meter, whiteboard tools, randomizers, signals, reminders, and more.",
  widgets: [
    {
      id: "timer",
      title: "Timer",
    },
    {
      id: "point-tracker",
      title: "Point Tracker",
    },
    {
      id: "sound-meter",
      title: "Sound Meter",
    },
    {
      id: "whiteboard",
      title: "Whiteboard",
    },
    {
      id: "signal",
      title: "Signal",
    },
    {
      id: "randomizer",
      title: "Randomizer",
    },
  ],
};

export const TEACHER_HUB_SPOKES = {
  eyebrow: "Teacher Hub",
  headline: "Set up your day once. Let it run on screen.",
  subheading:
    "Schedules, routines, notes, events, and lesson materials all connect to your Present Screen, so your classroom display stays organized without extra tab juggling.",
  centralTitle: "Teacher Hub",
  centralCopy:
    "Set up the systems behind your classroom once, then let Lesson Launchpad carry them into daily routines.",
  optionalCta: "See how it works",
  spokes: [
    {
      id: "schedules",
      title: "Schedules",
      description: "Build calendar-based class schedules that keep every day in sync.",
      imageSrc: teacherHubScheduleManagerImage,
      imageAlt: "Schedule Manager calendar-based schedule setup",
    },
    {
      id: "automations",
      title: "Automations",
      description: "Build routine timelines for reminders, timers, transitions, and daily classroom routines.",
      imageSrc: "/images/whats-new/whats-new-automations-unified-after.png",
      imageAlt: "Automated Routine timeline with automation steps",
    },
    {
      id: "class-notes",
      title: "Class Notes",
      description: "Capture daily lesson notes and keep plans connected to each class.",
      imageSrc: "/images/whats-new/whats-new-class-notes-after.png",
      imageAlt: "Class Notes tab in Content Manager",
    },
    {
      id: "print-export",
      title: "Print & Export",
      description: "Turn class notes and lesson plans into printable handouts, sub plans, or shared PDFs.",
      imageSrc: "/images/whats-new/whats-new-print-to-export-after.png",
      imageAlt: "Class Notes Export button in Content Manager",
    },
    {
      id: "content-manager",
      title: "Content Manager",
      description: "Organize lesson content, resources, calendars, and classroom materials in one place.",
      imageSrc: "/images/whats-new/whats-new-content-manager-after.png",
      imageAlt: "Content Manager with category tabs",
    },
    {
      id: "events",
      title: "Events",
      description: "Import school events and keep important dates visible across your schedule and classroom display.",
      imageSrc: "/images/whats-new/whats-new-events-manager-after.jpg",
      imageAlt: "Events Manager in Teacher Hub",
    },
  ],
};

export const CLASS_STYLES = {
  eyebrow: "Class Styles",
  headline: "Make every class feel instantly recognizable.",
  subheading:
    "Give each class its own visual style so students immediately know what class or routine they are in.",
  videoSrc: classStylesPreviewVideo,
  videoPoster: classStylesPreviewPoster,
  imageAlt: "Class Styles dialog with preset and custom style options",
  highlights: [
    "Preset themes for quick setup",
    "Custom colors and backgrounds",
    "Consistent styling across class displays",
  ],
};

export const TESTIMONIALS = {
  headline: "Teachers are using Lesson Launchpad to make classroom routines smoother.",
  placeholders: [
    {
      id: "placeholder-1",
      quote: "[Placeholder: needs real quote] My students know exactly what to do when they walk in.",
      name: "Teacher testimonial",
      role: "Placeholder",
    },
    {
      id: "placeholder-2",
      quote: "[Placeholder: needs real quote] I stopped juggling timers and tabs.",
      name: "Teacher testimonial",
      role: "Placeholder",
    },
    {
      id: "placeholder-3",
      quote: "[Placeholder: needs real quote] Lesson Launchpad has become the center of my classroom.",
      name: "Teacher testimonial",
      role: "Placeholder",
    },
    {
      id: "placeholder-4",
      quote: "[Placeholder: needs real quote] It helps my classes stay consistent.",
      name: "Teacher testimonial",
      role: "Placeholder",
    },
  ],
};

export const TESTIMONIALS_EDITORIAL = {
  eyebrow: "WHAT TEACHERS ARE SAYING",
  headline: "Classroom routines, made easier.",
  subheading: "Real feedback from educators using Lesson Launchpad in their daily routines.",
  stories: [
    {
      id: "crissy-s",
      quote:
        "Lesson Launchpad has been a great one-stop spot to hold all my lessons and links for my classes. I work with all the students in our building with a 5-minute transition time between groups, and I can schedule my classes so they're up and ready while I'm getting things reset.",
      firstName: "Crissy",
      lastName: "S",
      gradeLevel: "Elementary",
      subject: "STEM",
      state: "Alabama",
      imageSrc: "/images/landing-v2/testimonial-classroom-display.png",
      imageAlt: "Lesson Launchpad classroom display shown on a Promethean board.",
    },
    {
      id: "jenny-a",
      quote:
        "I am a tech-savvy person, and for years I have been trying to create something that does THIS. Now, I can stop because someone else did it first - and better, I might add! I started using it last year, and it just keeps getting better!! So happy!!",
      firstName: "Jenny",
      lastName: "A",
      gradeLevel: "Middle School",
      subject: "Band and General Music",
      state: "Iowa",
      imageSrc: "/images/landing-v2/testimonial-music-technology.png",
      imageAlt: "Lesson Launchpad music technology presentation shown on a classroom display.",
    },
    {
      id: "marci-b",
      quote:
        "I was looking for a better way to automate my classroom and free myself from the board, and Lesson Launchpad is even better than I imagined! Since incorporating Lesson Launchpad into my classroom, my students are more engaged, I'm free to move about the room, and my planning has become a breeze!",
      firstName: "Marci",
      lastName: "B",
      gradeLevel: "Middle School",
      subject: "Math and Science",
      state: "Idaho",
      imageSrc: "/images/landing-v2/testimonial-classroom-agenda-display.png",
      imageAlt: "Lesson Launchpad classroom agenda and objective display shown on a Promethean board.",
    },
  ],
};

export const PRICING = {
  headline: "Ready to automate your classroom screen?",
  copy: "Try Lesson Launchpad free for 30 days. No credit card required.",
  primaryCta: "Start Your Free Trial",
  secondaryCta: "Watch Demo",
  districtLinkText: "Schools & Districts",
  districtLinkDescription: "Looking for district pricing?",
};

export const FAQ_ITEMS = [
  {
    id: "recreate",
    question: "Do I need to recreate my lessons?",
    answer:
      "No. You can use the resources you already have, including Google Slides, PDFs, PowerPoint files, Docs, videos, and more.",
  },
  {
    id: "smartboards",
    question: "Does it work on Smartboards and Promethean Boards?",
    answer:
      "Yes. Lesson Launchpad runs in a modern web browser and is designed for classroom displays.",
  },
  {
    id: "smartboard-required",
    question: "Do I have to have a Smartboard or Promethean Board?",
    answer:
      "No. You can use Lesson Launchpad on any classroom display that can show a web browser, including a projector, TV, monitor, or interactive board.",
  },
  {
    id: "download-required",
    question: "Do I have to download anything?",
    answer:
      "No. Lesson Launchpad runs in your web browser, so there is nothing to install or download.",
  },
  {
    id: "digital-whiteboards",
    question: "How is this different from other digital whiteboards?",
    answer:
      "Most digital whiteboards help you display or write on a screen and give you a few tools for class. Lesson Launchpad includes those tools too, but it's built around classroom routines and automation. It brings your schedule, lesson content, timers, reminders, and classroom tools together — then helps them run at the right time. It's the class secretary you wish you had.",
  },
  {
    id: "automate",
    question: "Can Lesson Launchpad automate my classroom schedule?",
    answer:
      "Yes. Lesson Launchpad can automate timers, reminders, class changes, birthdays, agendas, and more around your daily schedule. The calendar-based scheduling system works with almost any school schedule, including rotating days, early releases, and no-school days.",
  },
  {
    id: "why-not-free",
    question: "Why isn’t Lesson Launchpad free?",
    schemaAnswer:
      "Lesson Launchpad was created by a former teacher and is completely bootstrapped. There are no investors, large corporate owners, or outside funding sources behind it. It is supported directly by the educators and schools who choose to use it. Your subscription allows us to keep Lesson Launchpad free from advertising, maintain reliable and secure infrastructure, provide responsive, personal support, continually improve the product based on teacher feedback, and build a sustainable company that educators can rely on for years to come. Because Lesson Launchpad is funded by its users, our priorities remain aligned with teachers—not advertisers, investors, or the demands of a larger corporation. We keep the price intentionally affordable at $4.99 per month or $39.99 per year. Schools and districts can also purchase discounted licenses for their teachers. We believe the best relationship is a straightforward one: you pay for a product that makes your job easier, and our responsibility is to keep making that product better.",
    answer: (
      <>
        <p>
          Lesson Launchpad was created by a former teacher and is completely bootstrapped. There are no investors,
          large corporate owners, or outside funding sources behind it. It is supported directly by the educators and
          schools who choose to use it.
        </p>
        <p>Your subscription allows us to:</p>
        <ul>
          <li>Keep Lesson Launchpad free from advertising</li>
          <li>Maintain reliable and secure infrastructure</li>
          <li>Provide responsive, personal support</li>
          <li>Continually improve the product based on teacher feedback</li>
          <li>Build a sustainable company that educators can rely on for years to come</li>
        </ul>
        <p>
          Because Lesson Launchpad is funded by its users, our priorities remain aligned with teachers—not
          advertisers, investors, or the demands of a larger corporation.
        </p>
        <p>
          We keep the price intentionally affordable at $4.99 per month or $39.99 per year. Schools and districts can
          also purchase discounted licenses for their teachers.
        </p>
        <p>
          We believe the best relationship is a straightforward one: you pay for a product that makes your job easier,
          and our responsibility is to keep making that product better.
        </p>
      </>
    ),
  },
  {
    id: "trial",
    question: "Is there a free trial?",
    answer: "Yes. Teachers can start with a free 30-day trial. No credit card required.",
  },
];

export const FINAL_CTA = {
  headline: "Ready to automate your classroom screen?",
  primaryCta: "Start Your Free Trial",
  subtext: "Try Lesson Launchpad free for 30 days. No credit card required.",
};

export const DEMO_MODAL = {
  title: "Watch Demo",
  label: "Video needed: 90-second demo showing Lesson Launchpad classroom automation.",
};
